import {
  FC,
  useState,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
  PropsWithChildren,
  useEffect,
  ReactNode,
} from 'react'
import { User } from '../models/User'
import { useNavigate } from 'react-router-dom'
import { RegisterData } from '../models/AuthModel'
import * as UserLocalStorageHelper from '../helpers/UserLocalStorageHelper'
import {
  resetStore,
  logout as logoutAPI,
  updateLoggedUserInfos,
  getLoggedUserInfos,
  login as loginAPI,
  register as registerAPI,
  forgotPassword as forgotPasswordAPI,
  resetPassword as resetPassworddAPI,
} from '../helpers/AuthHelper'
import { useAppDispatch } from '../store/hooks'
import { Account } from '../models/Account'
import Gleap from 'gleap'
import { GleapIdentify } from '../services/GleapService'

type AuthContextProps = {
  currentUser: User | undefined
  updateCurrentUser: (user: User | undefined) => void
  isLogged: boolean | undefined
  setIsLogged: Dispatch<SetStateAction<boolean | undefined>>
  accounts: Account[]
  setAccounts: Dispatch<SetStateAction<Account[]>>
  register: (data: RegisterData) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  updateUser: (user: User) => Promise<boolean>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (
    email: string,
    token: string,
    password: string
  ) => Promise<void>
}

const initAuthContextPropsState = {
  currentUser: undefined,
  updateCurrentUser: () => {},
  isLogged: undefined,
  setIsLogged: () => {},
  accounts: [],
  setAccounts: () => {},
  register: () => Promise.resolve(),
  login: () => Promise.resolve(),
  logout: () => {},
  updateUser: ({}) => new Promise<boolean>(() => false),
  forgotPassword: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

interface AuthProviderProps {
  loadingLayout: ReactNode
}
const AuthProvider: FC<PropsWithChildren<AuthProviderProps>> = ({
  loadingLayout,
  children,
}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [currentUser, setCurrentUser] = useState<User | undefined>(undefined)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [isLogged, setIsLogged] = useState<boolean | undefined>(undefined)

  const updateCurrentUser = (user: User | undefined) => {
    setCurrentUser(user)
    if (user) {
      UserLocalStorageHelper.setUserID(user.id)
    } else {
      UserLocalStorageHelper.removeUserID()
    }
  }

  const register = async (data: RegisterData) => {
    try {
      await registerAPI(data)
      const { data: userResponse } = await getLoggedUserInfos()
      setIsLogged(true)
      updateCurrentUser(userResponse.data)

      // Third party
      GleapIdentify(userResponse.data)
    } catch (error) {
      setIsLogged(false)
      throw error
    }
  }

  const login = async (email: string, password: string) => {
    try {
      await loginAPI(email, password)
      const { data: userResponse } = await getLoggedUserInfos()
      setIsLogged(true)
      updateCurrentUser(userResponse.data)

      // Third party
      GleapIdentify(userResponse.data)

      // Redirect to logged part
      const queryParameters = new URLSearchParams(window.location.search)
      const returnTo = queryParameters.get('returnTo')
      navigate(returnTo ? returnTo : '/')
    } catch (error) {
      setIsLogged(false)
      throw error
    }
  }

  const forgotPassword = async (email: string) => {
    try {
      await forgotPasswordAPI(email)
    } catch (error) {
      throw error
    }
  }

  const resetPassword = async (
    email: string,
    token: string,
    password: string
  ) => {
    try {
      await resetPassworddAPI(email, token, password)
    } catch (error) {
      throw error
    }
  }

  const logout = () => {
    logoutAPI().finally(() => {
      // Local
      setIsLogged(false)
      updateCurrentUser(undefined)
      dispatch(resetStore())

      // Third party
      Gleap.clearIdentity()

      // Finally redirect to login
      navigate('/auth/login')
    })
  }

  const updateUser = (user: User) =>
    updateLoggedUserInfos(user).then((_) => {
      getLoggedUserInfos().then((data) => updateCurrentUser(data.data.data))
      return true
    })

  useEffect(() => {
    const localUserId = UserLocalStorageHelper.getUserID()
    setIsLogged(localUserId !== undefined)
  }, [])

  if (isLogged === undefined) {
    return loadingLayout
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        updateCurrentUser,
        isLogged,
        setIsLogged,
        accounts,
        setAccounts,
        register,
        login,
        logout,
        updateUser,
        forgotPassword,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthProvider, useAuth }
